import { Text } from 'designSystem/components/Text'
import { CitedResponseSegments } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/search/CitedResponseSegments'
import { getAgenticConversationTurnStatus } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/agenticConversation'
import {
  getAgenticResponseBodyText,
  getAgenticResponseStatusText,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/agenticSearchText'
import type {
  AgenticConversationResponseEvent,
  AgenticConversationThoughtEvent,
  AgenticConversationTurn,
} from 'features/knowledgeDocumentViewer/types'
import React from 'react'

import { AgenticCollapsibleThoughts } from './AgenticCollapsibleThoughts'
import { AgenticMessageActions } from './AgenticMessageActions'
import {
  ChatBody,
  ChatResponseText,
  ChatTurnRoot,
  UserMessageBubble,
} from './agenticSearchChatStyles'

function getTurnCopyText({
  hasError,
  responseEvent,
}: {
  hasError: boolean
  responseEvent?: AgenticConversationResponseEvent
}): string {
  if (responseEvent) {
    const bodyText = getAgenticResponseBodyText(responseEvent)
    if (bodyText) return bodyText
    return getAgenticResponseStatusText(responseEvent)
  }
  if (hasError) {
    return 'Search failed. Try again.'
  }
  return ''
}

export function AgenticChatTurnRows({
  showActions = false,
  turn,
}: {
  showActions?: boolean
  turn: AgenticConversationTurn
}): React.ReactElement {
  const isLoading = getAgenticConversationTurnStatus(turn) === 'loading'
  const thoughts = turn.events.filter(
    (event): event is AgenticConversationThoughtEvent =>
      event.type === 'thought'
  )
  const responseEvent = turn.events.find(
    (event): event is AgenticConversationResponseEvent =>
      event.type === 'response'
  )
  const hasError = turn.events.some((event) => event.type === 'error')
  const showThoughts =
    isLoading || thoughts.length > 0 || responseEvent !== undefined
  const copyText = getTurnCopyText({ hasError, responseEvent })

  return (
    <ChatTurnRoot>
      <UserMessageBubble>{turn.query}</UserMessageBubble>
      <ChatBody>
        {showThoughts ? (
          <AgenticCollapsibleThoughts
            isThinking={isLoading}
            responseEvent={responseEvent}
            thoughts={thoughts}
          />
        ) : null}
        {responseEvent ? (
          <CitedResponseSegments
            event={responseEvent}
            ResponseText={ChatResponseText}
          />
        ) : null}
        {hasError ? (
          <Text as='p' variant='body' color='muted'>
            Search failed. Try again.
          </Text>
        ) : null}
        {showActions && !isLoading ? (
          <AgenticMessageActions copyText={copyText} retryQuery={turn.query} />
        ) : null}
      </ChatBody>
    </ChatTurnRoot>
  )
}
